import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { Loader2, ArrowLeft, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { useStudentAuth } from "@/lib/studentAuth";
import { formatApiErrorDetail } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Parses '342/500' -> {score: 342, max: 500}
function parseScore(text) {
  const m = (text || "").match(/(\d+)\s*\/\s*(\d+)/);
  if (!m) return null;
  return { score: parseInt(m[1], 10), max: parseInt(m[2], 10) };
}

function readNum(doc, id) {
  const el = doc.getElementById(id);
  if (!el) return 0;
  const n = parseInt((el.textContent || "0").replace(/[^\d-]/g, ""), 10);
  return Number.isFinite(n) ? n : 0;
}

export default function StudentExam() {
  const { student, authHeader } = useStudentAuth();
  const { examId } = useParams();
  const navigate = useNavigate();
  const iframeRef = useRef(null);
  const submittedRef = useRef(false);
  const observerRef = useRef(null);
  const pollerRef = useRef(null);
  const [submitState, setSubmitState] = useState("idle"); // idle | submitting | success | error
  const [submittedSummary, setSubmittedSummary] = useState(null);

  const handleResults = useCallback(async (doc, autoSubmitted = false) => {
    if (submittedRef.current) return;
    submittedRef.current = true;

    const finalScoreText = doc.getElementById("finalScore")?.textContent || "";
    const parsed = parseScore(finalScoreText);
    if (!parsed) {
      submittedRef.current = false;
      return;
    }

    // Try to read the latest rich attempt from the iframe's localStorage
    // (v2 HTML stores per-attempt under key 'eim_cma_mock_history_v1').
    let rich = null;
    try {
      const win = iframeRef.current?.contentWindow;
      const raw = win?.localStorage?.getItem("eim_cma_mock_history_v1");
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr) && arr.length) rich = arr[0];
      }
    } catch (e) {
      console.warn("[Exam] could not read iframe localStorage history", e);
    }

    const timeFromHTML = (doc.getElementById("finalTime")?.textContent || "").trim();
    const timeUsedStr = rich
      ? `${rich.timeUsedMin}m ${String(rich.timeUsedSec || 0).padStart(2, "0")}s`
      : timeFromHTML;

    const payload = {
      exam_id: examId,
      score: parsed.score,
      max_score: parsed.max,
      mcq_marks: rich ? rich.mcqMarks : readNum(doc, "mcqMarks"),
      case_marks: rich ? rich.caseMarks : readNum(doc, "caseMarks"),
      correct_count: rich ? rich.correct : readNum(doc, "finalCorrect"),
      total_questions: rich ? rich.totalQuestions : readNum(doc, "finalTotal"),
      time_used: timeUsedStr,
      auto_submitted: rich ? !!rich.autoSubmitted : autoSubmitted,
      part: rich?.part || "",
      section_stats: rich?.sectionStats || {},
      focus_areas: rich?.focusAreas || [],
    };
    setSubmitState("submitting");
    try {
      await axios.post(`${API}/student/exam-results`, payload, { headers: authHeader });
      setSubmitState("success");
      setSubmittedSummary(payload);
      toast.success(`Score saved: ${payload.score}/${payload.max_score}`);
    } catch (err) {
      setSubmitState("error");
      const msg = formatApiErrorDetail(err.response?.data?.detail) || "Failed to save result";
      toast.error(msg);
      submittedRef.current = false; // allow retry
    }
  }, [authHeader, examId]);

  const attachObserver = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe) {
      console.warn("[Exam] iframe ref missing on load");
      return;
    }
    let doc;
    try {
      doc = iframe.contentDocument || iframe.contentWindow?.document;
    } catch (e) {
      console.error("[Exam] cross-origin: cannot access iframe document", e);
      toast.error("Cannot read exam result (cross-origin). Please contact admin.");
      return;
    }
    if (!doc) {
      console.warn("[Exam] iframe doc null on first attach");
      return;
    }

    // Disconnect any previous observer / poller
    if (observerRef.current) observerRef.current.disconnect();
    if (pollerRef.current) clearInterval(pollerRef.current);

    const checkAndSubmit = (label) => {
      // Re-resolve doc each time (in case iframe navigates internally)
      let curDoc;
      try {
        curDoc = iframe.contentDocument || iframe.contentWindow?.document;
      } catch {
        return;
      }
      if (!curDoc) return;
      const resultsScreen = curDoc.getElementById("resultsScreen");
      if (!resultsScreen) return;
      const isActive = resultsScreen.classList.contains("active");
      const finalScoreEl = curDoc.getElementById("finalScore");
      const txt = finalScoreEl?.textContent || "";
      const parsed = parseScore(txt);
      if (isActive && parsed && parsed.score >= 0 && parsed.max > 0 && !submittedRef.current) {
        console.log(`[Exam] results detected via ${label}: ${txt}`);
        handleResults(curDoc, false);
      }
    };

    // 1) Observe the resultsScreen element's class change
    const resultsScreen = doc.getElementById("resultsScreen");
    if (resultsScreen) {
      const obs = new MutationObserver(() => checkAndSubmit("observer"));
      obs.observe(resultsScreen, { attributes: true, attributeFilter: ["class"] });
      observerRef.current = obs;
      console.log("[Exam] MutationObserver attached to #resultsScreen");
    } else {
      console.warn("[Exam] #resultsScreen not found in iframe doc");
    }

    // 2) Poll every 1.5s as a robust fallback (covers any race/observer-miss)
    pollerRef.current = setInterval(() => checkAndSubmit("poller"), 1500);

    // 3) Initial check in case results are already on screen
    checkAndSubmit("initial");
  }, [handleResults]);

  useEffect(() => {
    // Kick off observer attachment a bit after mount as a safety net,
    // in case the iframe `onLoad` handler didn't fire (e.g. it loaded
    // before the React listener was attached).
    const t = setTimeout(() => attachObserver(), 1500);
    return () => {
      clearTimeout(t);
      if (observerRef.current) observerRef.current.disconnect();
      if (pollerRef.current) clearInterval(pollerRef.current);
    };
  }, [attachObserver]);

  if (student === undefined) {
    return <div className="min-h-screen flex items-center justify-center bg-[#f8fafc]"><Loader2 className="w-8 h-8 animate-spin text-[#002570]" /></div>;
  }
  if (student === null) return <Navigate to="/student/login" replace />;

  const examSrc = `${API}/exams/${examId}/file`;

  return (
    <div className="min-h-screen bg-[#f8fafc] flex flex-col">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-[64px] flex items-center justify-between gap-4">
          <button
            onClick={() => {
              if (submitState === "submitting") return;
              if (!submittedRef.current && !window.confirm("Leave the exam? Your progress will be lost if not yet submitted.")) return;
              navigate("/student/dashboard");
            }}
            data-testid="exam-back-btn"
            className="inline-flex items-center gap-2 text-sm font-inter font-medium text-slate-600 hover:text-[#002570]"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Dashboard
          </button>
          <div className="flex items-center gap-3 font-inter text-sm">
            {submitState === "submitting" && <span className="inline-flex items-center gap-2 text-[#002570]" data-testid="exam-submit-status-submitting"><Loader2 className="w-4 h-4 animate-spin" /> Saving result…</span>}
            {submitState === "success" && submittedSummary && (
              <span className="inline-flex items-center gap-2 text-emerald-600" data-testid="exam-submit-status-success">
                <CheckCircle2 className="w-4 h-4" /> Result saved: {submittedSummary.score}/{submittedSummary.max_score}
              </span>
            )}
            {submitState === "error" && (
              <span className="inline-flex items-center gap-2 text-rose-600" data-testid="exam-submit-status-error">
                <AlertCircle className="w-4 h-4" /> Save failed (we'll retry on next completion)
              </span>
            )}
            <span className="hidden md:inline-flex text-slate-500">{student.name}</span>
          </div>
        </div>
      </header>

      <main className="flex-1">
        <iframe
          ref={iframeRef}
          title="Mock Exam"
          src={examSrc}
          onLoad={attachObserver}
          data-testid="exam-iframe"
          className="w-full"
          style={{ height: "calc(100vh - 64px)", border: 0, background: "white" }}
        />
      </main>
    </div>
  );
}
